import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { nextPlan, tokenBalance, usePlansQuery, useQuotaQuery } from '@entities/Plan';
import { paths } from '@shared/config';
import { cn } from '@shared/lib';
import { TokenIcon } from '@shared/ui';

type Variant = 'panel' | 'collapsed' | 'chip';

/**
 * Баланс жетонов всегда на виду (решение владельца 2026-10-13): в боковой панели —
 * плашка над профилем, в свёрнутой — значок с числом, на мобиле — кнопка справа
 * сверху, в пару к «меню» (внизу справа живёт оранжевая «+ Карточка», рядом с ней
 * баланс бы сливался). Ведёт на «Тарифы». Полоса — остаток: пустеет с каждой тратой.
 * Несгораемые жетоны (с 2026-10-22) — «+N» оранжевым во всех видах: в свёрнутой панели второй строкой под дневными,
 * на кнопке телефона — рядом (владелец 2026-10-22: «в свернутом состоянии не видно сколько несгораемых»). Дневные
 * кончились, а несгораемые есть — ноль серым, не красным: тратить ещё есть что.
 */
export function TokenBalance({ variant, onNavigate }: { variant: Variant; onNavigate?: () => void }) {
  const { t } = useTranslation();
  const { data: quota } = useQuotaQuery();
  const { data: plans } = usePlansQuery();
  if (!quota) return null;

  const b = tokenBalance(quota);
  const title = [t('quota.balanceTitle', { left: b.left, limit: b.limit }), b.bonus > 0 ? t('quota.bonusTitle', { count: b.bonus }) : '']
    .filter(Boolean)
    .join(' ');
  const tone = b.out ? 'bg-danger' : b.low ? 'bg-warn' : 'bg-accent';
  // Дневные кончились, а несгораемые есть — ноль серым, а не красным.
  const onBonus = b.out && b.bonus > 0;
  // Подпись про тариф (владелец 2026-11-04): спокойно, рядом с балансом. Кончились — что будет дальше и что даёт следующий.
  const current = plans?.find((p) => p.tier === quota.plan);
  const next = nextPlan(plans, quota.plan);
  const caption = !current
    ? null
    : b.out && !onBonus
      ? next
        ? t('quota.outNext', { plan: next.title, limit: next.dailyGenerationLimit })
        : t('quota.outLast')
      : t('quota.planLine', { plan: current.title, limit: b.limit });
  const dailyTone = onBonus ? 'text-muted' : b.out ? 'text-[#f47a7e]' : 'text-fg';

  if (variant === 'chip') {
    return (
      <NavLink
        to={paths.plans}
        onClick={onNavigate}
        aria-label={title}
        title={title}
        // Цель 44 px по высоте (приёмка A1-16); середина — на 28 px, в пару к гамбургеру.
        className="fixed right-3 top-1.5 z-30 flex h-11 items-center gap-1.5 rounded-xl border border-line bg-[#0e0e10] pl-2 pr-3 text-[15px] font-semibold text-fg shadow-[0_6px_16px_-4px_rgba(0,0,0,0.5)]"
      >
        <TokenIcon size={22} />
        <span className={dailyTone}>{b.left}</span>
        {b.bonus > 0 && <span className="text-[13px] font-semibold text-accent">+{b.bonus}</span>}
      </NavLink>
    );
  }

  if (variant === 'collapsed') {
    return (
      <NavLink
        to={paths.plans}
        onClick={onNavigate}
        aria-label={title}
        title={title}
        className={cn(
          'mx-auto mb-2 flex w-12 flex-col items-center justify-center gap-0.5 rounded-[14px] bg-card transition-colors hover:bg-elev',
          b.bonus > 0 ? 'h-16' : 'h-14',
        )}
      >
        <TokenIcon size={22} />
        <span className={cn('text-[12px] font-semibold leading-none', dailyTone)}>{b.left}</span>
        {b.bonus > 0 && <span className="text-[11px] font-semibold leading-none text-accent">+{b.bonus}</span>}
      </NavLink>
    );
  }

  return (
    <NavLink
      to={paths.plans}
      onClick={onNavigate}
      title={title}
      className="mb-2 flex items-center gap-3 rounded-[14px] bg-card p-3 transition-colors hover:bg-elev"
    >
      <TokenIcon size={30} />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-1.5">
          <b className={cn('text-[15px] font-semibold', b.out && !onBonus ? 'text-[#f47a7e]' : 'text-fg')}>{b.left}</b>
          <span className="truncate text-[12px] text-muted">{t('quota.outOf', { limit: b.limit })}</span>
          {/* Панель узкая: «+N» без подписи, что это несгораемые — в подсказке и для скринридера. */}
          {b.bonus > 0 && <span className="ml-auto shrink-0 text-[12px] font-semibold text-accent">+{b.bonus}</span>}
        </span>
        <span className="mt-1.5 block h-1 overflow-hidden rounded-sm bg-line" aria-hidden="true">
          <span
            className={cn('block h-full rounded-sm transition-[width] duration-500', tone)}
            style={{ width: `${(b.share * 100).toFixed(1)}%` }}
          />
        </span>
        {caption ? <span className="mt-1.5 block text-[11.5px] leading-4 text-muted">{caption}</span> : null}
        <span className="sr-only">{title}</span>
      </span>
    </NavLink>
  );
}
